import React, { useState, useEffect } from 'react'
import { createLogger } from './logger'
import {
  getDebugEnabled,
  subscribeToDebug,
  registerComponent,
  isComponentHighlighted,
  subscribeToHighlights,
  getComponentColor,
  formatPropValue
} from './debugCore'

const logger = createLogger('DebugWrapper')

/**
 * Debug Wrapper HOC
 * 
 * Wraps React components with visual debugging aids:
 * - Colored borders around components
 * - Component name and file path overlays
 * - Props inspector on hover
 * - Click to log component details
 * 
 * Only active when VITE_DEBUG_MODE=true and debug enabled at runtime.
 * Zero overhead in production builds.
 * 
 * Usage:
 *   export default debugWrapper(MyComponent, 'MyComponent', 'src/components/MyComponent.jsx')
 * 
 * @param {Component} Component - React component to wrap
 * @param {string} displayName - Display name for debugging
 * @param {string} filePath - Relative file path
 * @returns {Component} Wrapped component with debug features (or original if disabled)
 */
export function debugWrapper(Component, displayName, filePath) {
  // If debug not enabled at build time, return original component
  if (import.meta.env.VITE_DEBUG_MODE !== 'true') {
    return Component
  }

  // Generate unique component ID
  const componentId = `${displayName}-${filePath}`

  const DebugWrappedComponent = (props) => {
    const [debugEnabled, setDebugEnabled] = useState(getDebugEnabled())
    const [showProps, setShowProps] = useState(false)
    const [highlighted, setHighlighted] = useState(isComponentHighlighted(componentId))

    useEffect(() => {
      // Register this component
      registerComponent(componentId, displayName, filePath)
      
      const unsubDebug = subscribeToDebug(setDebugEnabled)
      const unsubHighlight = subscribeToHighlights(() => {
        setHighlighted(isComponentHighlighted(componentId))
      })
      
      return () => {
        unsubDebug()
        unsubHighlight()
      }
    }, [])

    // If debug disabled at runtime, render without wrapper
    if (!debugEnabled) {
      return <Component {...props} />
    }

    // If debug enabled but component not highlighted, render without visual wrapper
    if (!highlighted) {
      return <Component {...props} />
    }

    const handleClick = (e) => {
      e.stopPropagation()
      logger.info(`Component: ${displayName}`, {
        filePath,
        props,
        propsCount: Object.keys(props).length
      })
    }

    const handleMouseEnter = () => setShowProps(true)
    const handleMouseLeave = () => setShowProps(false)

    // Get component color
    const colors = getComponentColor(displayName)

    return (
      <div
        className="debug-wrapper"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          position: 'relative',
          border: `2px solid ${colors.border}`,
          margin: '2px',
          backgroundColor: colors.background,
          cursor: 'pointer'
        }}
      >
        {/* Component header */}
        <div
          className="debug-header"
          style={{
            position: 'absolute',
            top: '-2px',
            left: '-2px',
            right: '-2px',
            backgroundColor: colors.border,
            color: 'white',
            padding: '2px 6px',
            fontSize: '10px',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            zIndex: 10000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {displayName}
        </div>

        {/* File path tooltip */}
        <div
          className="debug-path"
          style={{
            position: 'absolute',
            bottom: '-2px',
            left: '-2px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            padding: '2px 6px',
            fontSize: '9px',
            fontFamily: 'monospace',
            zIndex: 10000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {filePath}
        </div>

        {/* Props inspector */}
        {showProps && Object.keys(props).length > 0 && (
          <div
            className="debug-props"
            style={{
              position: 'absolute',
              top: '20px',
              right: '0',
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: '#0f0',
              padding: '8px',
              fontSize: '10px',
              fontFamily: 'monospace',
              zIndex: 10001,
              maxWidth: '300px',
              maxHeight: '400px',
              overflow: 'auto',
              borderRadius: '4px',
              border: '1px solid #0f0',
              pointerEvents: 'none'
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#fff' }}>
              Props ({Object.keys(props).length}):
            </div>
            {Object.entries(props).map(([key, value]) => {
              const displayValue = formatPropValue(value)

              return (
                <div key={key} style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#ff0' }}>{key}:</span>{' '}
                  <span style={{ color: '#0ff' }}>{displayValue}</span>
                </div>
              )
            })}
          </div>
        )}

        <Component {...props} />
      </div>
    )
  }

  DebugWrappedComponent.displayName = `Debug(${displayName})`
  return DebugWrappedComponent
}

// Re-export components for convenience
export { DebugToggle, ComponentSelectorButton } from './debugComponents'

export default debugWrapper
